/** @jsxImportSource @emotion/react */
import {
  Card,
  createStyles,
  Snackbar,
  Typography,
  useTheme,
  UtilityButton,
} from '@quickbit/qb-design-system';
import {
  useKYCDocumentImages,
  GetImageFunction,
  GetImagesFunction,
} from 'hooks/useKYCDocumentImages';

interface Props {
  userId: string;
  getKycDocumentImage: GetImageFunction;
  getKycDocumentImages: GetImagesFunction;
}

export const KycDocumentImages = ({
  userId,
  getKycDocumentImage,
  getKycDocumentImages,
}: Props) => {
  const styles = useStyles();
  const { palette } = useTheme();

  const {
    images: kycDocumentImages,
    error: kycDocumentImagesError,
    loadMore,
    loading: kycDocumentImagesLoading,
    hasMoreImages,
  } = useKYCDocumentImages(
    userId,
    5,
    getKycDocumentImage,
    getKycDocumentImages
  );

  return (
    <>
      {kycDocumentImages.length !== 0 && (
        <Card>
          <Typography variant="bodySmallest" color={palette.grey.x400}>
            Kyc Document Images
          </Typography>
          <div css={styles.imageContainer}>
            {kycDocumentImages.map((image) => (
              <div
                key={image.fileName}
                onClick={() => void window.open(image.fileUrl)}
              >
                <img src={image.fileUrl} />
                <span>
                  <p>
                    <b>File name: </b>
                    {image.fileName}
                  </p>
                  <p>
                    <b>Issuing country: </b>
                    {image.issuingCountryCode}
                  </p>
                  <p>
                    <b>Saved at: </b>
                    {image.savedAt.toLocaleString()}
                  </p>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
      {hasMoreImages && (
        <UtilityButton
          text={
            kycDocumentImages.length > 0 ? 'Load more images' : 'Get KYC images'
          }
          onClick={() => loadMore()}
          loading={kycDocumentImagesLoading}
        />
      )}
      <Snackbar
        variant="danger"
        open={!!kycDocumentImagesError}
        message={'Something went wrong while fetching KYC document images'}
      />
    </>
  );
};

const useStyles = createStyles(({ palette, spacing, shape }) => ({
  imageContainer: {
    display: 'flex',
    gap: spacing.xxxs,
    flexWrap: 'wrap',
    marginTop: spacing.xxxs,
    div: {
      height: '100%',
      border: `1px solid ${palette.grey.x200}`,
      borderRadius: shape.borderRadius,
      padding: `calc(${spacing.xxxs}/2)`,
      flex: `0 0 calc(33% - ${spacing.xxxs})`,
      img: {
        width: '100%',
        height: '100%',
        cursor: 'pointer',
      },
      p: {
        margin: 0,
      },
    },
  },
}));
